import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

interface ObjectiveSignals {
  responseLength: number;
  tokenEfficiency: number;
  responseSpeed: number;
  errorFree: boolean;
}

interface JudgeScore {
  id: string;
  criterionName: string;
  score: number;
  reasoning: string;
  judgeProvider: string;
  judgeModel: string;
  weightedScore: number;
}

interface Response {
  id: string;
  modelId: string;
  modelName: string;
  providerName: string;
  status: string;
  objectiveSignals: ObjectiveSignals;
  judgeScores: JudgeScore[];
}

interface Judge {
  provider: string;
  model: string;
}

interface DemoData {
  comparison: {
    id: string;
    prompt: string;
    createdAt: string;
  };
  responses: Response[];
  judges: Judge[];
  stats: {
    totalResponses: number;
    totalScores: number;
    avgScoresPerResponse: number;
    judgeCount: number;
  };
  featureFlags: {
    llmJudgeEnabled: boolean;
    shadowMode: boolean;
  };
}

export default function DemoScoringPage() {
  const { comparisonId } = useParams<{ comparisonId: string }>();
  const [data, setData] = useState<DemoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      if (!comparisonId) {
        setError('No comparison ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/results/demo/${comparisonId}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const json = await response.json();
        setData(json);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [comparisonId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading scoring data...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Error Loading Data</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{error || 'No data available'}</p>
                  <p className="mt-2">Comparison ID: {comparisonId || 'none'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { comparison, responses, judges, stats, featureFlags } = data;

  // Group scores by criterion to show judge agreement
  const scoresByCriterion: Record<string, JudgeScore[]> = {};
  responses.forEach(resp => {
    resp.judgeScores.forEach(score => {
      if (!scoresByCriterion[score.criterionName]) {
        scoresByCriterion[score.criterionName] = [];
      }
      scoresByCriterion[score.criterionName].push(score);
    });
  });

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🔬 Scoring Engine Demo
          </h1>
          <p className="text-gray-600">
            Internal validation page for multi-judge scoring system
          </p>
        </div>

        {/* Feature Flag Status */}
        <div className={`mb-6 rounded-lg p-4 border ${
          featureFlags.shadowMode
            ? 'bg-yellow-50 border-yellow-200'
            : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">
                {featureFlags.shadowMode ? '👻 Shadow Mode Active' : '✅ Feature Enabled'}
              </h3>
              <p className="text-sm text-gray-700 mt-1">
                {featureFlags.shadowMode
                  ? 'Scores stored in database but not shown to users'
                  : 'LLM Judge scoring is live for all users'}
              </p>
            </div>
            <code className="bg-gray-900 text-white px-3 py-1 rounded text-sm">
              ENABLE_LLM_JUDGE={featureFlags.llmJudgeEnabled.toString()}
            </code>
          </div>
        </div>

        {/* Comparison Details */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Comparison Details</h2>
          <div className="space-y-2">
            <p className="text-sm">
              <span className="font-medium text-gray-700">ID:</span>{' '}
              <code className="bg-gray-100 px-2 py-1 rounded text-xs">{comparison.id}</code>
            </p>
            <p className="text-sm">
              <span className="font-medium text-gray-700">Prompt:</span>{' '}
              <span className="text-gray-600">{comparison.prompt}</span>
            </p>
            <p className="text-sm">
              <span className="font-medium text-gray-700">Created:</span>{' '}
              <span className="text-gray-600">{new Date(comparison.createdAt).toLocaleString()}</span>
            </p>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.judgeCount}</div>
            <div className="text-sm text-gray-600">Judges</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-green-600">{stats.totalResponses}</div>
            <div className="text-sm text-gray-600">Responses</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.totalScores}</div>
            <div className="text-sm text-gray-600">Total Scores</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="text-2xl font-bold text-orange-600">{stats.avgScoresPerResponse.toFixed(2)}</div>
            <div className="text-sm text-gray-600">Avg Scores/Response</div>
          </div>
        </div>

        {/* Active Judges */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Active Judges (S6-S7)</h2>
          <div className="grid grid-cols-2 gap-4">
            {judges.map((judge, idx) => (
              <div key={idx} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                  {judge.provider[0].toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-gray-900">{judge.provider}</div>
                  <div className="text-xs text-gray-600">{judge.model}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Responses with Scores */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-gray-900">Response Scoring Details</h2>

          {responses.map((response, idx) => (
            <div key={response.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {response.providerName} - {response.modelName}
                  </h3>
                  <p className="text-sm text-gray-600">Status: {response.status}</p>
                </div>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {response.judgeScores.length} scores
                </span>
              </div>

              {/* Objective Signals */}
              <div className="mb-4 p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-2">Objective Signals (S5)</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-green-700">📏 Response Length:</span>{' '}
                    <span className="font-medium">{response.objectiveSignals.responseLength} chars</span>
                  </div>
                  <div>
                    <span className="text-green-700">🎯 Token Efficiency:</span>{' '}
                    <span className="font-medium">{response.objectiveSignals.tokenEfficiency} tokens</span>
                  </div>
                  <div>
                    <span className="text-green-700">⚡ Response Speed:</span>{' '}
                    <span className="font-medium">{(response.objectiveSignals.responseSpeed / 1000).toFixed(2)}s</span>
                  </div>
                  <div>
                    <span className="text-green-700">✅ Error Free:</span>{' '}
                    <span className="font-medium">{response.objectiveSignals.errorFree ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* Judge Scores */}
              {response.judgeScores.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Judge Scores by Criterion</h4>
                  <div className="space-y-2">
                    {Array.from(new Set(response.judgeScores.map(s => s.criterionName))).map(criterion => {
                      const criterionScores = response.judgeScores.filter(s => s.criterionName === criterion);
                      const avgScore = criterionScores.reduce((sum, s) => sum + s.score, 0) / criterionScores.length;

                      return (
                        <div key={criterion} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm font-medium text-gray-700">{criterion}</span>
                          <div className="flex items-center space-x-2">
                            <div className="flex space-x-1">
                              {criterionScores.map((score, i) => (
                                <span
                                  key={i}
                                  className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded"
                                  title={`${score.judgeProvider}: ${score.score}`}
                                >
                                  {score.score.toFixed(2)}
                                </span>
                              ))}
                            </div>
                            <span className="text-sm font-bold text-gray-900">
                              Avg: {avgScore.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pipeline Info */}
        <div className="mt-8 bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-purple-900 mb-4">Scoring Pipeline</h2>
          <div className="space-y-2 text-sm text-purple-800">
            <p><span className="font-medium">S4:</span> Multi-judge cross-evaluation with {judges.length} providers</p>
            <p><span className="font-medium">S4:</span> Trim logic removes top/bottom 10% outlier scores (≥5 judges)</p>
            <p><span className="font-medium">S4:</span> Reliability weighting (0.5x self-judges, 1.0x cross-judges)</p>
            <p><span className="font-medium">S5:</span> Objective signals extracted from response metadata</p>
            <p><span className="font-medium">S5:</span> Blended score: 70% judge scores + 30% objective signals</p>
            <p><span className="font-medium">S6-S7:</span> Expanded to 4 judge providers for better coverage</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 border-t border-gray-200 pt-4">
          <p className="text-xs text-gray-500 text-center">
            Internal demo page for scoring engine validation • Tags: scoring-engine-s8-complete
          </p>
        </div>
      </div>
    </div>
  );
}
